import { StrictMode } from 'react'
import * as Sentry from '@sentry/react'
import './i18n'
import { createRoot } from 'react-dom/client'
import './fonts'
import './index.css'
import App from './App.tsx'
import { initAnalytics } from './lib/analytics'

// ---------- Sentry (error monitoring) ----------
// Capture runtime errors + unhandled promise rejections + React render errors
// (via the ErrorBoundary below) and ship them to Sentry. DSN is the
// VITE_SENTRY_DSN build-time env var (baked into bundle — fine, DSN is a
// write-only credential; anyone with it can SEND events but can't read
// captured errors).
//
// Filters (beforeSend below) drop expected typed errors that the UI already
// surfaces gracefully — InsufficientCredits, NetworkError, etc. Those aren't
// bugs, they're known failure modes with their own UX paths. Sending them
// to Sentry would burn the free-tier quota on noise.
//
// What we don't enable yet (can add later):
//   - Performance monitoring (tracesSampleRate=0): would add lots of events
//   - Session Replay: heavy quota cost
//   - Source map uploads (@sentry/vite-plugin): stack traces would be from
//     the minified bundle. Add later for nicer debug ergonomics.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    // 'production' on the deployed Firebase build; 'development' locally
    // via `npm run dev`. Lets us filter Sentry's UI by environment.
    environment: import.meta.env.MODE,
    // Exception-only capture for now. Bump to e.g. 0.05 to sample 5% of
    // page loads + API calls for performance monitoring once we have quota
    // headroom.
    tracesSampleRate: 0.0,
    // Don't auto-attach default PII. We set Sentry user manually from
    // useAuth via Sentry.setUser elsewhere if/when we want per-user tagging.
    sendDefaultPii: false,
    beforeSend(event, hint) {
      // Drop known-not-bugs so the Sentry feed is signal, not noise.
      const err = hint.originalException as { name?: string } | undefined
      if (err && typeof err === 'object' && 'name' in err) {
        // Our typed errors from lib/api.ts that already have UX paths
        const expected = [
          'InsufficientCreditsError',
          'NetworkError',
          'ContentRejectedError',
          'TransientUpstreamError',
          'GenerationTimeoutError',
        ]
        if (expected.includes(err.name as string)) return null
      }
      return event
    },
  })
  console.log(`[SENTRY] initialized (environment=${import.meta.env.MODE})`)
} else if (import.meta.env.PROD) {
  // Production build without a DSN means we forgot to set it as a GitHub
  // Action secret. Surfacing this in console so it doesn't silently miss.
  console.warn('[SENTRY] VITE_SENTRY_DSN not set — error monitoring disabled in production')
}

// ---------- PostHog (product analytics) ----------
// Behavior + funnel tracking (where users drop off in onboarding, etc.).
// Fully inert until VITE_POSTHOG_KEY is set — see src/lib/analytics.ts.
initAnalytics()

// Sentry.ErrorBoundary wraps the React tree. When a render error occurs
// (e.g. a component throws during render), Sentry catches it, logs the
// error to Sentry, and renders the fallback UI instead of crashing the
// whole app. Without this, render errors would unmount the entire React
// tree and leave a blank page.
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
          background: '#fafafb',
        }}>
          <div style={{
            maxWidth: '480px',
            textAlign: 'center',
            background: 'white',
            padding: '40px 32px',
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: '#fee2e2',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '20px',
              fontSize: '32px',
            }}>⚠️</div>
            <h1 style={{ fontSize: '20px', fontWeight: 700, margin: '0 0 8px', color: '#1a1a23' }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px', lineHeight: 1.5 }}>
              The page hit an unexpected error. We've been notified and will look into it. Try refreshing — most issues resolve on retry.
            </p>
            <button
              onClick={resetError}
              style={{
                padding: '10px 24px',
                borderRadius: '999px',
                background: '#d6297d',
                color: 'white',
                border: 'none',
                fontSize: '14px',
                fontWeight: 700,
                cursor: 'pointer',
                marginRight: '8px',
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.href = '/'}
              style={{
                padding: '10px 24px',
                borderRadius: '999px',
                background: '#f3f4f6',
                color: '#1a1a23',
                border: 'none',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Go home
            </button>
          </div>
        </div>
      )}
    >
      <App />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
